import React, { useState } from 'react';
import { StyleSheet, View, type ImageSourcePropType, type LayoutChangeEvent } from 'react-native';
import { TintedIcon } from './TintedIcon';

/**
 * Узор на матовой панели шапки.
 *
 * <p><b>Почему картинка, а не SVG.</b> Оба тайла нарисованы как повторяющийся рисунок целиком
 * (примерно по сорок значков на плитку, разбросанных без сетки) — такой узор описывается
 * сотнями путей, и складывать его из фигур в рантайме дороже, чем один раз положить PNG.
 * Повтор берёт на себя сама платформа: {@code resizeMode="repeat"} тиражирует плитку по всей
 * панели, поэтому нам не надо знать ни её ширину, ни высоту.
 *
 * <p><b>Почему монохром и {@code tintColor}.</b> Плитки лежат чёрными линиями по прозрачному
 * фону и красятся темой. Так один файл обслуживает четыре палитры: тёмный контур на светлых
 * темах, светлый на тёмных, тёплый янтарь поверх ночного леса. Красит {@link TintedIcon} —
 * у него уже решена беда react-native-web, где перекраска не доезжала до экрана без
 * пересоздания {@code Image}.
 *
 * <p><b>Плотности.</b> Рядом с каждым файлом лежат {@code @2x} и {@code @3x}; Metro сам берёт
 * нужный по плотности экрана. Базовый файл — 300 px, поэтому одна плитка занимает 300 dp:
 * в шапку помещается примерно полтора повтора, и рисунок читается узором, а не набором
 * случайных значков (при плитке в 150 dp значки мельчали до нечитаемых штрихов).
 *
 * <p><b>Почему узор приходится центрировать руками.</b> {@code resizeMode="repeat"} кладёт
 * первую плитку в левый верхний угол и дальше тиражирует её вправо и вниз. Целое число
 * повторов в панель не влезает никогда (плитка 300 dp, ширина шапки у телефона около 390),
 * поэтому весь остаток обрезался с одной стороны: слева узор начинался ровно с края плитки,
 * справа обрывался на середине. Глазом это читается как «обои уехали влево». Ширину панели
 * заранее знать нельзя — она зависит от устройства, — так что меряем её через
 * {@code onLayout} и сдвигаем полотно так, чтобы обрезки по обоим краям были одинаковыми.
 */
const SOURCES: Record<'mail' | 'varangian', ImageSourcePropType> = {
  mail: require('../../assets/patterns/header-mail.png'),
  varangian: require('../../assets/patterns/header-varangian.png'),
};

/** Сторона плитки в dp: базовый PNG — 300×300, `@2x`/`@3x` лежат рядом. */
const TILE = 300;

/**
 * Сдвиг полотна по одной оси, при котором обрезанные плитки по краям равны.
 *
 * <p>Панель длиной {@code extent} вмещает несколько целых плиток и остаток
 * {@code r = extent % TILE}. Чтобы остаток разошёлся поровну, по краю должно оставаться
 * {@code r / 2} — то есть начинать тиражирование надо за {@code TILE - r / 2} до края.
 * При {@code r === 0} выходит ровно {@code -TILE}, и плитки ложатся встык, как и раньше.
 */
function centeringOffset(extent: number): number {
  return -(TILE - (extent % TILE) / 2);
}

export function HeaderPattern({
  kind,
  tint,
  opacity,
}: {
  kind: 'mail' | 'varangian';
  tint: string;
  opacity: number;
}) {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);

  const onLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setSize((prev) => (prev && prev.width === width && prev.height === height ? prev : { width, height }));
  };

  return (
    // Обёртка нужна ради pointerEvents (узор лежит поверх заливки панели, и без этого он
    // перехватывал бы касания у кнопок шапки, которые рисуются следом) и ради overflow:
    // полотно намеренно шире панели, и вылезающий край надо обрезать по её границам.
    <View style={[StyleSheet.absoluteFill, styles.clip]} pointerEvents="none" onLayout={onLayout}>
      {size ? (
        <TintedIcon
          source={SOURCES[kind]}
          tintColor={tint}
          resizeMode="repeat"
          style={{
            position: 'absolute',
            left: centeringOffset(size.width),
            top: centeringOffset(size.height),
            // Полотно длиннее панели ровно на плитку — столько «съедает» сдвиг влево и вверх.
            width: size.width + TILE,
            height: size.height + TILE,
            opacity,
          }}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  clip: { overflow: 'hidden' },
});
